import CopilotSuggestionAPI from '../../api/copilotSuggestions';

// Keyed by TURN, never by conversation alone. Under a burst of customer messages the websocket
// event for turn N and the GET for turn N+1 arrive out of order, and a conversation-keyed store
// lets the later write lose to the earlier one -- the card then shows an answer to a message that
// is no longer the one on screen, or vanishes on reload.
const AWAIT_PUSH_TIMEOUT_MS = 200000;

const turnKey = (conversationId, lastCustomerMessageId) =>
  `${conversationId}:${lastCustomerMessageId}`;

const state = {
  byTurn: {},
  uiFlags: {},
  generations: {},
};

const emptyFlags = {
  isFetching: false,
  isRequesting: false,
  isAwaitingPush: false,
  refusal: null,
  fetchError: null,
};

const generationMark = suggestion => ({
  status: suggestion.status,
  content: suggestion.content,
  failureReason: suggestion.failure_reason,
});

const generationLanded = (from, suggestion) => {
  if (from === undefined) return false;
  if (from === null) return true;
  const now = generationMark(suggestion);
  return (
    now.status !== from.status ||
    now.content !== from.content ||
    now.failureReason !== from.failureReason
  );
};

const getters = {
  // The card asks for the turn it is rendering, so a stale suggestion cannot be returned by
  // accident: a key that does not match the message on screen simply has nothing in it.
  getByTurn: $state => (conversationId, lastCustomerMessageId) =>
    $state.byTurn[turnKey(conversationId, lastCustomerMessageId)] || null,
  getFlagsByTurn: $state => (conversationId, lastCustomerMessageId) =>
    $state.uiFlags[turnKey(conversationId, lastCustomerMessageId)] ||
    emptyFlags,
};

const mutations = {
  SET_SUGGESTION(currentState, suggestion) {
    if (!suggestion) return;
    const key = turnKey(
      suggestion.conversation_id,
      suggestion.last_customer_message_id
    );
    currentState.byTurn = { ...currentState.byTurn, [key]: suggestion };
  },
  MARK_GENERATED(currentState, key) {
    const generations = currentState.generations || {};
    currentState.generations = {
      ...generations,
      [key]: (generations[key] || 0) + 1,
    };
    const flags = currentState.uiFlags[key];
    if (flags?.isAwaitingPush) {
      currentState.uiFlags = {
        ...currentState.uiFlags,
        [key]: { ...flags, isAwaitingPush: false },
      };
    }
  },
  SET_FLAGS(currentState, { key, flags }) {
    const current = currentState.uiFlags[key] || emptyFlags;
    currentState.uiFlags = {
      ...currentState.uiFlags,
      [key]: { ...current, ...flags },
    };
  },
  CLEAR_TURN(currentState, key) {
    const rest = { ...currentState.byTurn };
    delete rest[key];
    currentState.byTurn = rest;
  },
};

const applyForeignTurn = (commit, moduleState, data) => {
  const dataKey = turnKey(data.conversation_id, data.last_customer_message_id);
  if (moduleState.byTurn[dataKey]) return;
  if (moduleState.uiFlags?.[dataKey]?.isFetching) return;
  commit('SET_SUGGESTION', data);
};

const applyFetched = (
  commit,
  moduleState,
  { key, before, generationsBefore, data }
) => {
  if (turnKey(data.conversation_id, data.last_customer_message_id) !== key) {
    applyForeignTurn(commit, moduleState, data);
    return;
  }
  const flags = moduleState.uiFlags?.[key];
  const landed =
    flags?.isAwaitingPush && generationLanded(flags.awaitingFrom, data);
  const generationPushed =
    (moduleState.generations?.[key] || 0) !== generationsBefore;
  if (moduleState.byTurn[key] === before || (landed && !generationPushed)) {
    commit('SET_SUGGESTION', data);
  }
  if (landed) commit('SET_FLAGS', { key, flags: { isAwaitingPush: false } });
};

const actions = {
  // Fetched on mount, not only pushed over the websocket: otherwise a `ready` suggestion renders
  // only for an agent who already had the conversation open when the job finished, and reopening
  // the conversation shows nothing.
  async fetch(
    { commit, state: moduleState },
    { conversationId, lastCustomerMessageId }
  ) {
    const key = turnKey(conversationId, lastCustomerMessageId);
    const before = moduleState.byTurn[key];
    const generationsBefore = moduleState.generations?.[key] || 0;
    commit('SET_FLAGS', {
      key,
      flags: { isFetching: true, refusal: null, fetchError: null },
    });
    try {
      const { data, status } = await CopilotSuggestionAPI.get(conversationId);
      if (status === 204 || !data) {
        if (moduleState.byTurn[key] === before) commit('CLEAR_TURN', key);
      } else {
        applyFetched(commit, moduleState, {
          key,
          before,
          generationsBefore,
          data,
        });
      }
    } catch (error) {
      // Swallowed on purpose: a suggestion that cannot be fetched is a suggestion the agent never
      // asked for. Failing loudly here would put an error card on a conversation the agent is
      // trying to answer.
      if (moduleState.byTurn[key] === before) commit('CLEAR_TURN', key);
      commit('SET_FLAGS', {
        key,
        flags: { fetchError: error?.message || 'fetch_failed' },
      });
    } finally {
      commit('SET_FLAGS', { key, flags: { isFetching: false } });
    }
  },

  // On demand and regenerate are the same call; the server decides which one it is and refuses
  // with a reason the card shows.
  async request(
    { commit, dispatch, state: moduleState },
    { conversationId, lastCustomerMessageId }
  ) {
    const key = turnKey(conversationId, lastCustomerMessageId);
    const generationsOf = () => moduleState.generations?.[key] || 0;
    const before = generationsOf();
    const snapshot = moduleState.byTurn[key];
    commit('SET_FLAGS', { key, flags: { isRequesting: true, refusal: null } });
    try {
      await CopilotSuggestionAPI.request(conversationId);
      if (generationsOf() === before) {
        const awaitingToken = Symbol('awaiting');
        commit('SET_FLAGS', {
          key,
          flags: {
            isAwaitingPush: true,
            awaitingFrom: snapshot ? generationMark(snapshot) : null,
            awaitingToken,
          },
        });
        setTimeout(() => {
          dispatch('expireWait', {
            conversationId,
            lastCustomerMessageId,
            awaitingToken,
          });
        }, AWAIT_PUSH_TIMEOUT_MS);
      }
    } catch (error) {
      commit('SET_FLAGS', {
        key,
        flags: { refusal: error?.response?.data?.error || 'unknown' },
      });
    } finally {
      commit('SET_FLAGS', { key, flags: { isRequesting: false } });
    }
  },

  async expireWait(
    { commit, dispatch, state: moduleState },
    { conversationId, lastCustomerMessageId, awaitingToken }
  ) {
    const key = turnKey(conversationId, lastCustomerMessageId);
    const stillWaiting = () => {
      const flags = moduleState.uiFlags?.[key];
      return flags?.isAwaitingPush && flags.awaitingToken === awaitingToken;
    };
    if (!stillWaiting()) return;
    await dispatch('fetch', { conversationId, lastCustomerMessageId });
    if (stillWaiting()) {
      commit('SET_FLAGS', { key, flags: { isAwaitingPush: false } });
    }
  },

  async recordOutcome({ commit }, { conversationId, outcome, suggestionId }) {
    try {
      const { data } = await CopilotSuggestionAPI.recordOutcome(
        conversationId,
        outcome,
        suggestionId
      );
      commit('SET_SUGGESTION', data);
    } catch (error) {
      // The outcome is analytics, not the agent's job. A lost one must not break the reply flow.
    }
  },

  // Called by the websocket handler. Same mutation as the fetch, so the two paths cannot disagree.
  pushed({ commit }, suggestion) {
    if (!suggestion) return;
    const key = turnKey(
      suggestion.conversation_id,
      suggestion.last_customer_message_id
    );
    commit('SET_SUGGESTION', suggestion);
    if (suggestion.generated === true) commit('MARK_GENERATED', key);
  },
};

export default { namespaced: true, state, getters, actions, mutations };
export { actions, getters, mutations, turnKey, AWAIT_PUSH_TIMEOUT_MS };
